<!-- @format -->
<template>
    <div class="two-factor-auth-widget">
        <div class="widget-header">
            <h3>Two - Factor Authentication</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <div v-if="isLoading" class="loading-card">
                <loading-svg />
            </div>
            <div class="description">
                <h3>Text Message Authentication</h3>
                <p>Add a Phone Number to get verification code to your mobile when signing in.</p>
            </div>
            <div class="form-container">
                <div class="phone-container">
                    <label>Phone Number</label>
                    <phone-input
                        id="phoneNumber1"
                        v-model="phoneNumber"
                        clearable
                        defaultCountryCode="US"
                        :error="!isValid"
                        @update="onUpdate"
                    />
                </div>
                <div v-show="isWaitingForVerificationCode" class="verification-code-container">
                    <app-input
                        v-model="verificationCode"
                        :isValid="!verificationCodeError"
                        description="We've sent a verification code to your provided phone number. It may take a
                        few minutes to get there."
                        :error="verificationCodeError"
                        label="Verification Code"
                    />
                </div>
            </div>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="cancel-btn transparent-btn" @click="closeWidget">
                    Cancel
                </app-button>
            </div>
            <div class="action-container">
                <app-button
                    v-show="!isWaitingForVerificationCode"
                    :disabled="!canResendCode || !isValid"
                    class="save-btn"
                    @click="handleSendVerificationCode"
                >
                    {{ sendVerificationLabel }}
                </app-button>
                <app-button
                    v-show="isWaitingForVerificationCode"
                    class="save-btn"
                    size="secondary"
                    :disabled="!canResendCode"
                    @click="handleSendVerificationCode"
                >
                    {{ resendCodeLabel }}
                </app-button>
                <app-button
                    v-show="isWaitingForVerificationCode"
                    class="save-btn"
                    @click="handleVerifyVerificationCode"
                    >Verify
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const PhoneInput = () => import('@/components/phone-country-input/phone-country-selector.vue');
const AppInput = () => import('@/components/common/app-input');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'two-factor-auth-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'phone-input': PhoneInput,
        'app-input': AppInput,
        'loading-svg': LoadingSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            phoneNumber: this.widget.phoneNumber,
            verificationCode: null,
            verificationCodeId: null,
            isWaitingForVerificationCode: false,
            formattedNumber: '',
            verificationCodeError: null,
            isValid: null,
            isLoading: false,
        };
    },

    computed: {
        resendCodeLabel() {
            return this.$store.state.multiFactorAuthentication.canResendCode
                ? 'Resend Code'
                : `Resend Code in ${this.$store.state.multiFactorAuthentication.timer}`;
        },
        sendVerificationLabel() {
            return this.$store.state.multiFactorAuthentication.canResendCode
                ? 'Send Verification Code'
                : `Send Verification Code in ${this.$store.state.multiFactorAuthentication.timer}`;
        },
        canResendCode() {
            return this.$store.state.multiFactorAuthentication.canResendCode;
        },
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        onUpdate({ formattedNumber, isValid }) {
            this.isValid = isValid;
            this.formattedNumber = formattedNumber;
        },

        async handleSendVerificationCode() {
            this.verificationCodeError = null;
            if (this.isValid) {
                this.isWaitingForVerificationCode = true;
                this.isLoading = true;
                try {
                    const result = await this.$api.post('/api/Mfa/Methods', {
                        type: 'SMS',
                        endpoint: this.formattedNumber,
                    });
                    this.verificationCodeId = result.data.Id;
                } catch (error) {
                } finally {
                    this.resendCodeCountdown();
                    this.isLoading = false;
                }
            }
        },

        async handleVerifyVerificationCode() {
            try {
                this.isLoading = true;
                await this.$api.post(`/api/Mfa/Methods/${this.verificationCodeId}/Activate`, {
                    code: this.verificationCode,
                });
                EventBus.$emit('Mfa:success');
                this.closeWidget();
            } catch (error) {
                this.verificationCodeError = 'Invalid Code';
            } finally {
                this.isLoading = false;
            }
        },

        resendCodeCountdown() {
            const countDownSeconds = 30;
            this.$store.state.multiFactorAuthentication.canResendCode = false;
            this.$store.state.multiFactorAuthentication.timer = countDownSeconds;
            const duration = this.$moment.duration(countDownSeconds, 's');

            const timerIntervalId = setInterval(() => {
                duration.subtract(1, 's');
                this.$store.state.multiFactorAuthentication.timer = duration.seconds();

                if (duration.asMilliseconds() !== 0) {
                    return;
                }

                clearInterval(timerIntervalId);
                this.$store.state.multiFactorAuthentication.timer = null;
                this.$store.state.multiFactorAuthentication.canResendCode = true;
            }, 1000);
        },
    },
};
</script>

<style>
.two-factor-auth-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.two-factor-auth-widget .cancel-btn:hover {
    background: #e1e0e5;
}

.two-factor-auth-widget .content-wrapper .loading-card {
    position: absolute;
    display: flex;
    justify-content: center;
    top: 0;
    left: 0;
    width: 100%;
    align-items: center;
    background: #00000025;
    border-radius: 0 20px 20px 0;
    height: 100%;
    z-index: 1;
}

.two-factor-auth-widget .content-wrapper .description {
    margin-bottom: 30px;
}

.two-factor-auth-widget .content-wrapper .description h3 {
    font: 700 18px/24px 'Nunito', sans-serif;
    color: #382153;
}

.two-factor-auth-widget .content-wrapper .description p {
    font: normal 16px/24px 'Roboto', sans-serif;
    color: #382153;
}

.two-factor-auth-widget .content-wrapper .app-border-section .top-text {
    font: 18px 'Roboto', sans-serif;
    font-weight: 500;
}

.two-factor-auth-widget .form-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.two-factor-auth-widget .form-container label {
    font-weight: 500;
}

.two-factor-auth-widget .verification-code-container span {
    margin-top: 10px;
    display: inline-block;
    color: #382153;
    font-size: 16px;
}

.two-factor-auth-widget .action-container {
    display: flex;
    gap: 19px;
}
</style>
